import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getPDPVariations, createPDPVariation, updatePDPVariation, deletePDPVariation, uploadPDPScreenshot, downloadFileBlob } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Browser, Plus, Trash, Pencil, Image, CalendarBlank, ArrowLeft, ArrowRight, X, ArrowsOut
} from '@phosphor-icons/react';

export default function PDPVariationsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [variations, setVariations] = useState([]);
  const [screenshotUrls, setScreenshotUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', date: '', notes: '', file: null });
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const scrollRef = useRef(null);

  const loadData = useCallback(async () => {
    try {
      const res = await getPDPVariations();
      setVariations(res.data);
      // Load screenshots
      const urls = {};
      for (const v of res.data) {
        if (v.screenshot) {
          try {
            urls[v.id] = await downloadFileBlob(v.screenshot);
          } catch (err) {
            console.error('Screenshot load error:', err.message);
          }
        }
      }
      setScreenshotUrls(urls);
    } catch (err) {
      console.error('Failed to load PDP variations:', err.message);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-scroll to rightmost (latest) on load
  useEffect(() => {
    if (!loading && scrollRef.current && variations.length > 0) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [loading, variations.length]);

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.date) return;
    setSubmitting(true);
    try {
      if (editId) {
        await updatePDPVariation(editId, { name: form.name, date: form.date, notes: form.notes || null });
        if (form.file) {
          await uploadPDPScreenshot(editId, form.file);
        }
      } else {
        const res = await createPDPVariation({ name: form.name, date: form.date, notes: form.notes || null });
        if (form.file && res.data.id) {
          await uploadPDPScreenshot(res.data.id, form.file);
        }
      }
      setForm({ name: '', date: '', notes: '', file: null });
      setShowAddForm(false);
      setEditId(null);
      await loadData();
    } catch (err) {
      console.error(err);
      alert('Failed to save variation');
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this PDP variation?')) return;
    try {
      await deletePDPVariation(id);
      loadData();
    } catch (err) { console.error(err); }
  };

  const handleScreenshotUpload = async (varId) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      try {
        await uploadPDPScreenshot(varId, file);
        loadData();
      } catch (err) { console.error(err); }
    };
    input.click();
  };

  const startEdit = (v) => {
    setEditId(v.id);
    setForm({ name: v.name, date: v.date, notes: v.notes || '' });
    setShowAddForm(true);
  };

  const scrollBy = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 340, behavior: 'smooth' });
    }
  };

  if (loading) return <div className="p-8 text-[#555555]">Loading PDP variations...</div>;

  return (
    <div className="p-6 lg:p-8" data-testid="pdp-variations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Browser size={22} weight="fill" className="text-[#32B4AC]" />
            <h1 className="font-['Manrope'] text-2xl font-bold tracking-tight text-[#0F0F0F]">PDP Variations & Changes</h1>
          </div>
          <p className="text-sm text-[#555555]">Visual history of Product Detail Page iterations over time</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setShowAddForm(!showAddForm); setEditId(null); setForm({ name: '', date: '', notes: '', file: null }); }}
            className="h-9 px-4 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] transition-colors flex items-center gap-2"
            data-testid="add-pdp-variation-btn"
          >
            <Plus size={14} weight="bold" />
            Add Variation
          </button>
        )}
      </div>

      {/* Add/Edit Form (Admin only) */}
      {showAddForm && isAdmin && (
        <div className="border border-[#32B4AC] rounded-md p-5 mb-6 bg-[#32B4AC]/5" data-testid="pdp-variation-form">
          <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#32B4AC] mb-4">
            {editId ? 'Edit Variation' : 'New Variation'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Name *</Label>
              <Input data-testid="pdp-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., V1 - Original Layout" className="border-[#E8E8E8] rounded-md h-10" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Date *</Label>
              <Input data-testid="pdp-date-input" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="border-[#E8E8E8] rounded-md h-10" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Notes (optional)</Label>
              <Input data-testid="pdp-notes-input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Key changes in this version" className="border-[#E8E8E8] rounded-md h-10" />
            </div>
          </div>
          <div className="mb-4">
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Screenshot</Label>
            <label className="flex items-center gap-2 px-4 py-2.5 border border-[#E8E8E8] rounded-md text-sm text-[#555555] hover:bg-[#F4F4F4] cursor-pointer transition-colors w-fit" data-testid="pdp-screenshot-input">
              <Image size={16} />
              {form.file ? form.file.name : 'Choose screenshot...'}
              <input type="file" accept="image/*" className="hidden" onChange={e => setForm(p => ({ ...p, file: e.target.files[0] || null }))} />
            </label>
            {form.file && <button type="button" onClick={() => setForm(p => ({ ...p, file: null }))} className="text-xs text-[#555555] hover:text-[#FF3B30] mt-1">Remove</button>}
          </div>
          <div className="flex gap-2">
            <button onClick={handleSubmit} disabled={submitting || !form.name.trim() || !form.date} className="h-9 px-4 bg-[#32B4AC] text-white text-sm font-semibold rounded-md hover:bg-[#2A9B94] disabled:opacity-50" data-testid="pdp-save-btn">
              {submitting ? 'Saving...' : (editId ? 'Update' : 'Create')}
            </button>
            <button onClick={() => { setShowAddForm(false); setEditId(null); }} className="h-9 px-4 border border-[#E8E8E8] text-sm rounded-md hover:bg-[#F4F4F4]" data-testid="pdp-cancel-btn">Cancel</button>
          </div>
        </div>
      )}

      {/* Timeline direction label */}
      {variations.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-[#555555] flex items-center gap-1"><ArrowLeft size={12} /> Oldest</span>
          <div className="flex gap-2">
            <button onClick={() => scrollBy(-1)} className="p-1.5 border border-[#E8E8E8] rounded-md hover:bg-[#F4F4F4] transition-colors" data-testid="scroll-left-btn"><ArrowLeft size={16} className="text-[#555555]" /></button>
            <button onClick={() => scrollBy(1)} className="p-1.5 border border-[#E8E8E8] rounded-md hover:bg-[#F4F4F4] transition-colors" data-testid="scroll-right-btn"><ArrowRight size={16} className="text-[#555555]" /></button>
          </div>
          <span className="text-xs text-[#555555] flex items-center gap-1">Latest <ArrowRight size={12} /></span>
        </div>
      )}

      {/* Horizontal scroll timeline */}
      {variations.length > 0 ? (
        <div ref={scrollRef} className="overflow-x-auto pb-4" style={{ scrollBehavior: 'smooth' }} data-testid="pdp-timeline">
          <div className="flex gap-5 items-start" style={{ minWidth: 'max-content' }}>
            {variations.map((v, idx) => (
              <div key={v.id} className="w-[360px] shrink-0 border border-[#E8E8E8] rounded-md overflow-hidden bg-white hover:border-[#1A1A1A] transition-colors group" data-testid={`pdp-card-${v.id}`}>
                {/* Card header */}
                <div className="p-4 border-b border-[#F4F4F4]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold bg-[#32B4AC] text-white px-2 py-0.5 rounded-full">V{idx + 1}</span>
                    <h3 className="text-sm font-semibold text-[#0F0F0F]" data-testid={`pdp-name-${v.id}`}>{v.name}</h3>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-[#555555]">
                    <CalendarBlank size={12} />
                    {new Date(v.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  {v.notes && <p className="text-xs text-[#555555] mt-1.5 line-clamp-2">{v.notes}</p>}
                </div>

                {/* Full screenshot — no cropping, natural aspect ratio */}
                <div className="relative bg-[#F4F4F4]">
                  {screenshotUrls[v.id] ? (
                    <>
                      <img
                        src={screenshotUrls[v.id]}
                        alt={v.name}
                        className="w-full h-auto block"
                        loading="lazy"
                        style={{ imageRendering: 'auto' }}
                      />
                      <button
                        onClick={() => setExpandedId(v.id)}
                        className="absolute top-2 right-2 p-1.5 bg-black/50 text-white rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`expand-pdp-${v.id}`}
                      >
                        <ArrowsOut size={14} />
                      </button>
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-20">
                      <Image size={32} className="text-[#E8E8E8] mb-2" />
                      <p className="text-xs text-[#555555]">No screenshot</p>
                      {isAdmin && (
                        <button onClick={() => handleScreenshotUpload(v.id)} className="text-xs text-[#32B4AC] hover:underline mt-1" data-testid={`upload-pdp-screenshot-${v.id}`}>Upload</button>
                      )}
                    </div>
                  )}
                </div>

                {/* Admin actions */}
                {isAdmin && (
                  <div className="flex items-center gap-2 p-3 border-t border-[#F4F4F4]">
                    {screenshotUrls[v.id] && (
                      <button onClick={() => handleScreenshotUpload(v.id)} className="text-[10px] text-[#32B4AC] hover:underline flex items-center gap-1" data-testid={`replace-pdp-screenshot-${v.id}`}>
                        <Image size={10} /> Replace
                      </button>
                    )}
                    <button onClick={() => startEdit(v)} className="text-[10px] text-[#555555] hover:text-[#0F0F0F] flex items-center gap-1" data-testid={`edit-pdp-${v.id}`}>
                      <Pencil size={10} /> Edit
                    </button>
                    <button onClick={() => handleDelete(v.id)} className="text-[10px] text-[#555555] hover:text-[#FF3B30] flex items-center gap-1 ml-auto" data-testid={`delete-pdp-${v.id}`}>
                      <Trash size={10} /> Delete
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-16 border border-[#E8E8E8] rounded-md">
          <Browser size={40} className="text-[#E8E8E8] mx-auto mb-3" />
          <p className="text-sm text-[#555555]">No PDP variations recorded yet</p>
          {isAdmin && <p className="text-xs text-[#555555] mt-1">Click "Add Variation" to start tracking PDP changes</p>}
        </div>
      )}

      {/* Fullscreen Modal */}
      {expandedId && screenshotUrls[expandedId] && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8" onClick={() => setExpandedId(null)} data-testid="pdp-fullscreen-modal">
          <button onClick={() => setExpandedId(null)} className="absolute top-4 right-4 p-2 bg-white/10 text-white rounded-md hover:bg-white/20" data-testid="close-fullscreen-btn">
            <X size={20} />
          </button>
          <div className="max-w-4xl max-h-full overflow-auto" onClick={e => e.stopPropagation()}>
            <img src={screenshotUrls[expandedId]} alt="PDP Full View" className="w-full rounded-md" />
            <div className="mt-3 text-center">
              <p className="text-white text-sm font-medium">{variations.find(v => v.id === expandedId)?.name}</p>
              <p className="text-white/60 text-xs">{new Date(variations.find(v => v.id === expandedId)?.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
